import type { Channel } from '@hilo/contracts';
import { useState, type CSSProperties } from 'react';
import { ChannelMark } from './ChannelMark';
import styles from './Avatar.module.css';

/** El aro del avatar es el hilo: discontinuo si está libre, firme si es tuya, gris si es de otra persona. */
export type AvatarThread = 'free' | 'mine' | 'other' | 'none';

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => /\p{L}/u.test(word));
  const letters = (words.length >= 2 ? [words[0]!, words[words.length - 1]!] : words.slice(0, 1))
    .map((word) => [...word].find((char) => /\p{L}/u.test(char)) ?? '');
  return letters.join('').toLocaleUpperCase('es') || '·';
}

/** Tono estable por persona (0–359) a partir de un identificador. */
export function hueOf(seed: string): number {
  let hash = 2166136261;
  for (const char of seed) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  // Ángulo áureo: identificadores parecidos dan tonos bien separados.
  return Math.round((hash % 1000) * 137.508) % 360;
}

/** Silueta neutra para quien no tiene nombre de perfil (en lugar de «?»). */
function PersonGlyph() {
  return (
    <svg className={styles.glyph} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
      strokeLinecap="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="9" r="3.6" />
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
    </svg>
  );
}

export function Avatar({ name, seed, thread = 'none', channel, photoUrl, size = 'md' }: {
  name: string | null;
  photoUrl?: string | null;
  seed: string;
  thread?: AvatarThread;
  channel?: Channel;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const hue = hueOf(seed);
  return (
    <span className={`${styles.avatar} ${styles[size]}`} data-thread={thread} aria-hidden="true"
      style={{ '--hue': hue } as CSSProperties}>
      <span className={styles.face}>{photoUrl && failedPhoto !== photoUrl
        ? <img className={styles.photo} src={photoUrl} alt="" onError={() => setFailedPhoto(photoUrl)} />
        : name?.trim() ? initialsOf(name) : <PersonGlyph />}</span>
      {channel && <span className={styles.channel}><ChannelMark channel={channel} size="xs" /></span>}
    </span>
  );
}
